// @ts-nocheck
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getSessionUser } from '@/lib/authSession';

export interface IssueAttachment {
  id: string;
  issue_id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export function useIssueAttachments(issueId: string) {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  const { data: attachments = [], isLoading, refetch } = useQuery({
    queryKey: ['issue-attachments', issueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_issue_attachments')
        .select('*')
        .eq('issue_id', issueId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as IssueAttachment[];
    },
    enabled: !!issueId,
  });

  const uploadAttachment = async (file: File) => {
    setIsUploading(true);
    try {
      const { data: { user } } = await getSessionUser();
      if (!user) throw new Error('Not authenticated');

      // Generate unique file path
      const fileExt = file.name.split('.').pop();
      const filePath = `${issueId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('issue-attachments')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create database record
      const { error: dbError } = await supabase
        .from('admin_issue_attachments')
        .insert({
          issue_id: issueId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: user.id,
        });

      if (dbError) throw dbError;

      toast.success('Attachment uploaded');
      refetch();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('Failed to upload attachment: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const deleteAttachmentMutation = useMutation({
    mutationFn: async (attachment: IssueAttachment) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('issue-attachments')
        .remove([attachment.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('admin_issue_attachments')
        .delete()
        .eq('id', attachment.id);

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      toast.success('Attachment deleted');
      queryClient.invalidateQueries({ queryKey: ['issue-attachments', issueId] });
    },
    onError: (error: any) => {
      toast.error('Failed to delete attachment: ' + error.message);
    },
  });

  const getPublicUrl = (filePath: string) => {
    const { data } = supabase.storage
      .from('issue-attachments')
      .getPublicUrl(filePath);
    return data.publicUrl;
  };

  return {
    attachments,
    isLoading,
    isUploading,
    uploadAttachment,
    deleteAttachment: deleteAttachmentMutation.mutate,
    isDeleting: deleteAttachmentMutation.isPending,
    getPublicUrl,
    refetch,
  };
}
